"use client";

import { cn } from "../lib/utils";
import { useCartAssistant } from "../lib/cartAssistant/CartAssistantProvider";
import { 
  ChevronDown, 
  MessageSquarePlus, 
  History,
  Check,
  Plus,
  Trash2,
  Loader2,
  Calendar,
  Clock,
  Edit2,
  Save,
  X as CloseIcon
} from "lucide-react";
import * as React from "react";
import { ConfirmationModal } from "../../components/common/ConfirmationModal";
import { ImageCacheService } from "../../services/imageCacheService";
import { useTranslation } from "react-i18next";
import { cleanupConfirmationsByThread, clearAllConfirmations } from "../lib/confirmations";
import { deleteItemImages } from "../../services/supabaseService";
import { useSupabase } from "../lib/SupabaseContext";
import { useUser } from "@clerk/clerk-react";

/**
 * Thread Dropdown component
 *
 * This component allows users to switch between different chat threads
 * and start new conversations.
 */

interface ThreadGroup {
  label: string;
  threads: any[];
}

export function ThreadDropdown({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const {
    currentThreadId,
    switchThread,
    startNewThread,
    messages: activeMessages,
    threads,
    threadsLoading: isLoading,
    refetchThreads: refetch,
    deleteThread,
    renameThread,
    loadThreadMessages,
  } = useCartAssistant();
  const supabaseClient = useSupabase();
  const { user } = useUser();
  const userKey = user?.id;
  const [isOpen, setIsOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState<string | null>(null);
  const [threadToDelete, setThreadToDelete] = React.useState<any | null>(null);
  
  // Editing state
  const [editingThreadId, setEditingThreadId] = React.useState<string | null>(null);
  const [editTitle, setEditTitle] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  // Helper to extract all possible image URLs from a thread's messages
  const extractImageUrlsFromThread = (thread: any): string[] => {
    const urls: string[] = [];
    if (!thread || !thread.messages || !Array.isArray(thread.messages)) return urls;

    // More permissive regex to catch Supabase URLs in various formats/encodings
    const supabaseRegex = /https:\/\/[^"']+\/storage\/v1\/object\/public\/items\/[^"'\s\]\)\\]+/g;
    const chatLocalRegex = /chat:[a-z0-9_]+/g;

    // Targeted traversal instead of stringifying the whole thread (avoids circular refs)
    thread.messages.forEach((msg: any) => {
      // Support both string content and content blocks array
      const contents = Array.isArray(msg.content) ? msg.content : [msg.content];
      
      contents.forEach((content: any) => {
        if (!content) return;

        // Case 1: Simple string content or text block
        const textToScan = typeof content === 'string' ? content : (content.type === 'text' ? content.text : null);
        if (typeof textToScan === 'string') {
          let match;
          // Reset regex state
          supabaseRegex.lastIndex = 0;
          while ((match = supabaseRegex.exec(textToScan)) !== null) {
            urls.push(match[0]);
          }

          // Extract chat: local markers
          chatLocalRegex.lastIndex = 0;
          while ((match = chatLocalRegex.exec(textToScan)) !== null) {
            urls.push(match[0]);
          }
        }

        // Case 2: Resource blocks (Direct URIs)
        if (typeof content === 'object' && content.type === 'resource' && content.resource) {
          const url = content.resource.uri || content.resource.url;
          if (typeof url === 'string') urls.push(url);
        }

        // Case 3: Component props (Confirmation cards, etc.)
        if (typeof content === 'object' && content.type === 'component' && content.props) {
          const props = content.props;
          if (typeof props.imageUrl === 'string') urls.push(props.imageUrl);
          
          // Check items in confirmation arrays
          if (Array.isArray(props.items)) {
            props.items.forEach((item: any) => {
              if (item && typeof item.imageUrl === 'string') urls.push(item.imageUrl);
            });
          }
        }
      });
    });

    // Clean up URLs (remove trailing characters that regex might have grabbed)
    return Array.from(new Set(urls.map(u => u.replace(/[,\.\\]+$/, ''))));
  };


  // Group threads by date
  const groupedThreads = React.useMemo(() => {
    const groups: ThreadGroup[] = [
      { label: t('chat.today'), threads: [] },
      { label: t('chat.yesterday'), threads: [] },
      { label: t('chat.earlier'), threads: [] }
    ];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    threads.forEach(thread => {
      const threadDate = new Date(thread.createdAt);
      threadDate.setHours(0, 0, 0, 0);

      if (threadDate.getTime() === today.getTime()) {
        groups[0].threads.push(thread);
      } else if (threadDate.getTime() === yesterday.getTime()) {
        groups[1].threads.push(thread);
      } else {
        groups[2].threads.push(thread);
      }
    });

    return groups.filter(g => g.threads.length > 0);
  }, [threads, t]);

  const confirmDelete = async () => {
    if (!threadToDelete) return;
    const threadId = threadToDelete.id;
    
    setIsDeleting(threadId);
    
    // Create a cleanup function to use in both success and error paths
    const finalize = () => {
      setIsDeleting(null);
      setThreadToDelete(null);
    };

    try {
      // 1. Cleanup related images from Supabase Storage (Non-blocking attempt)
      // We wrap this in its own try/catch and use a timeout promise to ensure 
      // Supabase storage issues don't hang the UI.
      if (supabaseClient) {
        try {
          const cleanupWithTimeout = Promise.race([
            (async () => {
              const threadWithMessages = { ...threadToDelete, messages: await loadThreadMessages(threadId) };
              const urls = extractImageUrlsFromThread(threadWithMessages);
              if (urls.length > 0) {
                await deleteItemImages(supabaseClient, urls, userKey);
                // Also cleanup local IndexedDB images
                await ImageCacheService.removeChatImages(urls);
              }
              // Also cleanup the per-thread ref registry
              await ImageCacheService.clearThreadImageRef(threadId);
            })(),
            new Promise((_, reject) => setTimeout(() => reject(new Error("Cleanup timeout")), 3000))
          ]);
          
          await cleanupWithTimeout;
        } catch (cleanupErr) {
          console.warn("Image cleanup was skipped or timed out:", cleanupErr);
        }
      }

      // 2. Delete the thread (The primary action)
      await deleteThread(threadId);
      
      // 3. Cleanup confirmation statuses
      cleanupConfirmationsByThread(threadId);
      
      // deleteThread() already resets the active thread and refetches the list
      finalize();
    } catch (err) {
      console.error("Failed to delete thread:", err);
      finalize(); // Ensure we don't stay stuck in deleting state on failure
    }
  };

  const startEditing = (e: React.MouseEvent, thread: any) => {
    e.stopPropagation();
    setEditingThreadId(thread.id);
    setEditTitle(thread.name || t('chat.untitled_chat'));
  };

  const saveTitle = async (e: React.MouseEvent, threadId: string) => {
    e.stopPropagation();
    if (!editTitle.trim() || isSaving) return;

    setIsSaving(true);
    try {
      await renameThread(threadId, editTitle.trim());
      setEditingThreadId(null);
    } catch (err) {
      console.error("Failed to update thread title:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const cancelEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingThreadId(null);
  };

  const handleClearAll = async () => {
    if (!window.confirm(t('chat.confirm_wipe_history'))) return;
    
    for (const thread of threads) {
      try {
        // Cleanup images for each thread
        if (supabaseClient) {
          const t = { ...thread, messages: await loadThreadMessages(thread.id).catch(() => []) };

          const urls = extractImageUrlsFromThread(t);
          if (urls.length > 0) {
            await deleteItemImages(supabaseClient, urls, userKey);
            // Also cleanup local IndexedDB images
            await ImageCacheService.removeChatImages(urls);
          }
          // Also cleanup the per-thread ref registry
          await ImageCacheService.clearThreadImageRef(thread.id);
        }
        await deleteThread(thread.id);
      } catch (err) {
        console.error(`Failed to delete thread ${thread.id}:`, err);
      }
    }
    
    // Clear all confirmation statuses since all threads are gone
    clearAllConfirmations();
    
    await refetch();
    startNewThread();
    setIsOpen(false);
  };

  return (
    <div className={cn("relative", className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-9 h-9 rounded-xl flex items-center justify-center transition-colors active:scale-95",
          isOpen ? "bg-emerald-50 text-emerald-600" : "text-gray-400 hover:bg-gray-100 hover:text-[#1a2b3c]"
        )}
        title={threads.find(t => t.id === currentThreadId)?.name || t('chat.history')}
        aria-label={t('chat.history')}
      >
        <History size={16} />
      </button>

      <ConfirmationModal 
        isOpen={!!threadToDelete}
        onClose={() => setThreadToDelete(null)}
        onConfirm={confirmDelete}
        title={t('chat.delete_session')}
        message={t('chat.delete_session_confirm', { 
          name: threadToDelete?.name || t('chat.untitled_chat'),
          date: threadToDelete ? new Date(threadToDelete.createdAt).toLocaleDateString(i18n.language) : '',
          count: (threadToDelete?.id === currentThreadId && activeMessages) ? activeMessages.length : (threadToDelete?.messages?.length || 0)
        })}
        confirmLabel={t('chat.wipe_chat')}
        cancelLabel={t('chat.keep_session')}
        variant="danger"
      />

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-[110] bg-black/10 backdrop-blur-[4px]" 
            onClick={() => setIsOpen(false)} 
          />
          <div className={cn(
            "fixed z-[120] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-top-4 duration-300",
            "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2", // Absolute center screen
            "w-[90vw] max-w-[360px]", // Responsive width
            "bg-white text-popover-foreground rounded-[2.5rem] border border-gray-100 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.2)]"
          )}>
            <div className="p-5 border-b border-gray-50 bg-gray-50/30 space-y-4">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                    <History size={14} />
                  </div>
                  <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-[#1a2b3c]">{t('chat.chat_history')}</h4>
                </div>
                {threads.length > 0 && (
                  <button
                    onClick={handleClearAll}
                    className="text-[9px] font-black uppercase text-gray-400 hover:text-rose-500 transition-colors flex items-center gap-1"
                  >
                    <Trash2 size={10} />
                    {t('chat.clear_all')}
                  </button>
                )}
              </div>
              
              <button
                onClick={() => {
                  startNewThread();
                  setIsOpen(false);
                }}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 text-[11px] font-black uppercase tracking-widest rounded-2xl bg-[#1a2b3c] text-white hover:bg-emerald-600 transition-all shadow-xl shadow-gray-200 active:scale-95 group"
              >
                <Plus className="h-4 w-4 group-hover:rotate-90 transition-transform duration-300" />
                {t('chat.start_new_session')}
              </button>
            </div>

            <div className="max-h-[400px] overflow-y-auto p-2 scrollbar-hide">
              {isLoading ? (
                <div className="p-12 text-center flex flex-col items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                  </div>
                  <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">{t('chat.syncing_history')}</span>
                </div>
              ) : threads.length === 0 ? (
                <div className="p-12 text-center flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-200">
                    <MessageSquarePlus size={24} />
                  </div>
                  <div className="space-y-1">
                    <span className="block text-[11px] font-black text-[#1a2b3c] uppercase tracking-widest">{t('chat.no_sessions')}</span>
                    <span className="block text-[9px] font-bold text-gray-400 uppercase tracking-tighter">{t('chat.sessions_desc')}</span>
                  </div>
                </div>
              ) : (
                groupedThreads.map((group) => (
                  <div key={group.label} className="mb-4 last:mb-2">
                    <div className="flex items-center gap-2 px-4 py-2 mb-1">
                      <Calendar size={10} className="text-emerald-500" />
                      <span className="text-[9px] font-black uppercase tracking-[0.2em] text-emerald-500/70">{group.label}</span>
                    </div>
                    
                    <div className="space-y-1">
                      {group.threads.map((thread) => {
                        return (
                          <div
                            key={thread.id}
                            className="group relative px-1"
                          >
                            {editingThreadId === thread.id ? (
                              <div className="flex items-center gap-2 w-full px-4 py-3 bg-emerald-50 rounded-2xl ring-1 ring-emerald-100 shadow-inner">
                                <input
                                  autoFocus
                                  value={editTitle}
                                  onChange={(e) => setEditTitle(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && saveTitle(e as any, thread.id)}
                                  className="flex-1 bg-transparent border-none focus:ring-0 text-sm font-bold text-emerald-900 p-0 placeholder:text-emerald-300"
                                  placeholder={t('chat.new_title')}
                                />
                                <button
                                  onClick={(e) => saveTitle(e, thread.id)}
                                  disabled={isSaving}
                                  className="p-1.5 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 transition-colors shadow-sm"
                                >
                                  {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  className="p-1.5 rounded-lg bg-white text-gray-400 hover:text-rose-500 transition-colors border border-emerald-100"
                                >
                                  <CloseIcon size={12} />
                                </button>
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    switchThread(thread.id);
                                    setIsOpen(false);
                                  }}
                                  className={cn(
                                    "flex items-center gap-3.5 w-full px-4 py-3.5 text-sm rounded-2xl transition-all text-left pr-20 relative overflow-hidden",
                                    currentThreadId === thread.id
                                      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 shadow-sm"
                                      : "hover:bg-gray-50 text-[#1a2b3c] hover:translate-x-1"
                                  )}
                                >
                                  {currentThreadId === thread.id && (
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" />
                                  )}
                                  
                                  <div className={cn(
                                    "w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-colors",
                                    currentThreadId === thread.id ? "bg-white text-emerald-500" : "bg-gray-50 text-gray-400 group-hover:bg-white group-hover:text-emerald-500 shadow-inner"
                                  )}>
                                    <Clock size={14} />
                                  </div>

                                  <div className="min-w-0 flex-1">
                                    <span className="block truncate font-black text-[12px] tracking-tight leading-none mb-1.5">
                                      {thread.name || t('chat.shopping_inquiry')}
                                    </span>
                                    <div className="flex items-center gap-2 opacity-60">
                                      <span className="block text-[8px] font-black uppercase tracking-wider text-gray-400">
                                        {new Date(thread.createdAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {currentThreadId === thread.id && (
                                    <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white shadow-lg shadow-emerald-200">
                                      <Check className="h-3 w-3" strokeWidth={3} />
                                    </div>
                                  )}
                                </button>
                                
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                                  <button
                                    onClick={(e) => startEditing(e, thread)}
                                    className="p-2 rounded-lg text-gray-300 hover:text-emerald-500 hover:bg-emerald-50 transition-all active:scale-90"
                                    title={t('common.edit')}
                                  >
                                    <Edit2 size={13} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setThreadToDelete(thread);
                                    }}
                                    disabled={isDeleting === thread.id}
                                    className="p-2 rounded-lg text-gray-300 hover:text-rose-500 hover:bg-rose-50 transition-all active:scale-90"
                                    title={t('common.delete')}
                                  >
                                    {isDeleting === thread.id ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Trash2 size={13} />
                                    )}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
